import { useEffect, useRef, useState } from "react";
import { Nav } from "react-bootstrap";
import { classMerge } from "../utility";
import styles from "./ReorderableNavList.module.css";

/**
 * Minimum shape required for an item rendered by ReorderableNavList.
 */
export interface ReorderableNavListItem {
  /** Unique identifier, used as the React key and drag payload */
  id: string;
  /** Display name shown on the tab */
  name: string;
}

/**
 * Props for the ReorderableNavList component.
 */
export interface ReorderableNavListProps<T extends ReorderableNavListItem> {
  /** Optional flag preventing reordering and disabling the tabs */
  disabled?: boolean;
  /** Optional flag preventing reordering while leaving the tabs enabled */
  disableReorder?: boolean;
  /** Optional function returning the Nav.Link eventKey for an item, defaults to the item index */
  getEventKey?: (item: T, index: number) => string;
  /** The ordered list of items rendered as navigation tabs */
  list: T[];
  /** Called with the reordered list when a tab has been dropped in a new position */
  setList: (ret: T[]) => void;
}

/**
 * Position and size of a tab, measured when a drag starts.
 */
interface TabRect {
  /** Left edge in viewport coordinates */
  left: number;
  /** Top edge in viewport coordinates */
  top: number;
  /** Rendered width of the tab */
  width: number;
  /** Rendered height of the tab */
  height: number;
}

/**
 * Tab measurements taken when a drag starts, before any preview offsets are applied.
 */
interface DragLayout {
  /** Rectangle of every tab, in list order */
  rects: TabRect[];
  /** Left edge of the tab strip, where wrapped rows begin */
  stripLeft: number;
  /** Right edge of the tab strip, beyond which tabs wrap onto a new row */
  stripRight: number;
}

/**
 * Simulates how the wrapping tab strip flows its tabs when they are placed in a given order.
 *
 * @param layout - Tab measurements taken when the drag started.
 * @param order - Original tab indices in the order they would be laid out.
 * @returns The top left position each tab would occupy, indexed by original tab index.
 */
const flowSlots = (layout: DragLayout, order: number[]): { left: number; top: number }[] => {
  const { rects, stripLeft, stripRight } = layout;
  const originTop = rects[0].top;
  const nextRow = rects.find((rect) => rect.top > originTop);
  const rowStep = nextRow ? nextRow.top - originTop : rects[0].height;
  const slots: { left: number; top: number }[] = [];
  // The first row may begin after tabs that are not reorderable, but later rows wrap to the strip edge
  let rowStartLeft = rects[0].left;
  let x = rowStartLeft;
  let row = 0;
  order.forEach((index) => {
    const { width } = rects[index];
    // A tab that starts a row never wraps, however wide it is
    if (x > rowStartLeft && x + width > stripRight + 0.5) {
      rowStartLeft = stripLeft;
      x = rowStartLeft;
      row += 1;
    }
    slots[index] = { left: x, top: originTop + row * rowStep };
    x += width;
  });
  return slots;
};

/**
 * Renders a list of items as draggable Bootstrap navigation tabs.
 *
 * While a tab is dragged the browser shows a copy of that tab as the drag image,
 * and the tabs it would displace slide to their new positions, wrapping onto other
 * rows where needed, so the resulting order is visible before the drop. Dropping
 * calls `setList` with a new array in the updated order; persisting that order is
 * the caller's responsibility.
 *
 * @param props - Component props.
 * @param props.disabled - Optional flag preventing reordering and disabling the tabs.
 * @param props.disableReorder - Optional flag preventing reordering while leaving the tabs enabled.
 * @param props.getEventKey - Optional function returning the Nav.Link eventKey for an item.
 * @param props.list - The ordered list of items rendered as navigation tabs.
 * @param props.setList - Called with the reordered list once a tab is dropped in a new position.
 * @returns Navigation items which can be reordered by dragging.
 */
export const ReorderableNavList = <T extends ReorderableNavListItem>({
  disabled = false,
  disableReorder = false,
  getEventKey,
  list,
  setList,
}: ReorderableNavListProps<T>): React.ReactElement => {
  /** Whether tabs may be picked up, as either flag blocks reordering */
  const canReorder = !disabled && !disableReorder;
  const [drag, setDrag] = useState<{ index: number; layout: DragLayout } | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  /** Rectangles of each tab, captured before any drag transforms are applied */
  const tabRects = useRef<TabRect[]>([]);
  /** Latest calculated drop index, read on drop because state updates lag drag events */
  const targetIndex = useRef<number | null>(null);
  /** Extent of the tab strip, used to ignore drops released outside it */
  const listBounds = useRef<{ left: number; right: number; top: number; bottom: number }>({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  });
  /** Removes the native drag listeners added when the drag started, whichever render created them */
  const cleanupListeners = useRef<(() => void) | null>(null);
  /** Index of the tab being dragged, mirrored in a ref for the native strip listeners */
  const activeDragIndex = useRef<number | null>(null);
  /** Whether the pointer was last seen over the tab strip, recorded during the drag */
  const overList = useRef(false);

  /**
   * Marks the whole tab strip as a valid drop zone. Without this the gaps between
   * tabs reject the drag and the browser shows the "not allowed" cursor even though
   * releasing there would still reorder the list.
   *
   * @param event - The native drag event raised anywhere over the tab strip.
   */
  const allowDrop = (event: DragEvent): void => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
    /* istanbul ignore else -- the strip only listens while a drag is in progress */
    if (activeDragIndex.current !== null) {
      const nextIndex = getTargetIndex(event.clientX, event.clientY, activeDragIndex.current);
      targetIndex.current = nextIndex;
      setOverIndex(nextIndex);
    }
  };

  /**
   * Records whether the pointer is still over the tab strip. Drag end reports
   * unreliable coordinates, so the last known position decides whether the move is
   * committed, and this runs on the document so positions outside the strip count.
   *
   * @param event - The native drag event raised anywhere in the document.
   */
  const trackPointer = (event: DragEvent): void => {
    overList.current =
      event.clientX >= listBounds.current.left &&
      event.clientX <= listBounds.current.right &&
      event.clientY >= listBounds.current.top &&
      event.clientY <= listBounds.current.bottom;
  };

  /**
   * Stops the tab strip accepting drops once the drag is over.
   */
  const releaseListeners = (): void => {
    cleanupListeners.current?.();
    cleanupListeners.current = null;
  };

  /**
   * Removes any listeners still attached if the list unmounts part way through a drag.
   */
  useEffect(
    () => () => {
      cleanupListeners.current?.();
    },
    [],
  );

  /**
   * Moves the dragged item to the target position and notifies the caller.
   *
   * @param from - Index of the item being dragged.
   * @param to - Index the item should be moved to.
   */
  const moveItem = (from: number, to: number): void => {
    if (from !== to) {
      const newList = [...list];
      const [moved] = newList.splice(from, 1);
      newList.splice(to, 0, moved);
      setList(newList);
    }
  };

  /**
   * Clears all drag tracking state once a drag has finished or been cancelled.
   */
  const endDrag = (): void => {
    setDrag(null);
    setOverIndex(null);
    tabRects.current = [];
    listBounds.current = { left: 0, right: 0, top: 0, bottom: 0 };
    targetIndex.current = null;
    overList.current = false;
    releaseListeners();
  };

  /**
   * Commits the pending move when the drag finishes. This runs on drag end rather
   * than only on drop because the sliding tabs can leave a gap beneath the pointer,
   * in which case no drop event is raised by any tab.
   */
  const commitDrag = (): void => {
    if (drag !== null && targetIndex.current !== null && overList.current) {
      moveItem(drag.index, targetIndex.current);
    }
    activeDragIndex.current = null;
    endDrag();
  };

  /**
   * Records the untransformed rectangle of every tab so that the drop position can
   * be calculated from the pointer while the tabs are being slid out of the way.
   *
   * @param source - The tab wrapper the drag started from.
   * @returns The tab measurements, used to preview the new order while dragging.
   */
  const captureLayout = (source: HTMLDivElement): DragLayout => {
    // The tabs are always rendered as div > Nav.Item > Nav, so the strip is present
    const strip = source.parentElement!.parentElement!;
    const wrappers = Array.from(strip.querySelectorAll<HTMLDivElement>("[data-reorderable-index]"));
    tabRects.current = wrappers.map((wrapper) => {
      const { left, top, width, height } = wrapper.getBoundingClientRect();
      return { left, top, width, height };
    });
    const stripRect = strip.getBoundingClientRect();
    listBounds.current = {
      left: stripRect.left,
      right: stripRect.right,
      top: stripRect.top,
      bottom: stripRect.bottom,
    };
    strip.addEventListener("dragover", allowDrop);
    strip.addEventListener("drop", allowDrop);
    document.addEventListener("dragover", trackPointer);
    // Later renders recreate the handlers, so removal must use these exact references
    cleanupListeners.current = () => {
      strip.removeEventListener("dragover", allowDrop);
      strip.removeEventListener("drop", allowDrop);
      document.removeEventListener("dragover", trackPointer);
    };
    return { rects: tabRects.current, stripLeft: stripRect.left, stripRight: stripRect.right };
  };

  /**
   * Works out which position the dragged tab would take if dropped at the pointer,
   * counting only the other tabs so that hovering over the dragged tab changes nothing.
   * The pointer belongs to the lowest row starting above it, so a gap between rows
   * still resolves by its horizontal position. Tabs in earlier rows, and tabs to the
   * left within that row, come before the drop position.
   *
   * @param clientX - Horizontal pointer position of the drag event.
   * @param clientY - Vertical pointer position of the drag event.
   * @param dragIndex - Index of the tab being dragged, also used when the layout could not be captured.
   * @returns The index the dragged tab should move to.
   */
  const getTargetIndex = (clientX: number, clientY: number, dragIndex: number): number => {
    let target = dragIndex;
    /* istanbul ignore else -- the layout is always captured before a drag reports a position */
    if (tabRects.current.length === list.length) {
      const rects = tabRects.current;
      const rowTop = rects.reduce(
        (top, rect) => (rect.top <= clientY && rect.top > top ? rect.top : top),
        rects[0].top,
      );
      target = rects.filter(
        (rect, index) =>
          index !== dragIndex &&
          (rect.top < rowTop || (rect.top === rowTop && rect.left + rect.width / 2 < clientX)),
      ).length;
    }
    return target;
  };

  /**
   * Uses a detached copy of the dragged tab as the drag image, so the item
   * following the cursor looks identical to the tab that was picked up.
   *
   * @param event - The drag start event raised by the tab wrapper.
   */
  const setDragImageFromTab = (event: React.DragEvent<HTMLDivElement>): void => {
    const source = event.currentTarget;
    const preview = source.cloneNode(true) as HTMLDivElement;
    preview.style.width = `${source.offsetWidth}px`;
    preview.classList.add(styles.reorderableNavDragImage);
    // The clone is decorative only, so it must not be discoverable as content
    preview.setAttribute("aria-hidden", "true");
    preview.removeAttribute("data-reorderable-index");
    document.body.appendChild(preview);
    const rect = source.getBoundingClientRect();
    event.dataTransfer.setDragImage(preview, event.clientX - rect.left, event.clientY - rect.top);
    // The preview must remain in the document until the browser has snapshotted it
    setTimeout(() => preview.remove(), 0);
  };

  /**
   * Works out how far every tab must move from its resting place to preview the
   * order the list would take if the dragged tab were dropped at the current target.
   *
   * @returns The horizontal and vertical offset of each tab, indexed by list position.
   */
  const getOffsets = (): { x: number; y: number }[] => {
    let offsets = list.map(() => ({ x: 0, y: 0 }));
    if (drag !== null && overIndex !== null) {
      const original = list.map((_, index) => index);
      const previewed = [...original];
      previewed.splice(drag.index, 1);
      previewed.splice(overIndex, 0, drag.index);
      const from = flowSlots(drag.layout, original);
      const to = flowSlots(drag.layout, previewed);
      offsets = from.map((slot, index) => ({
        x: to[index].left - slot.left,
        y: to[index].top - slot.top,
      }));
    }
    return offsets;
  };

  /**
   * Determines the styling a tab needs to show where the dragged tab will land.
   *
   * @param index - Index of the tab being rendered.
   * @param offset - Distance the tab must move to preview the new order.
   * @param offset.x - Horizontal distance to move.
   * @param offset.y - Vertical distance to move.
   * @returns The CSS module class for a dragged or displaced tab, or an empty string.
   */
  const getShiftClass = (index: number, offset: { x: number; y: number }): string => {
    let shiftClass = "";
    if (drag !== null && overIndex !== null) {
      if (index === drag.index) {
        shiftClass = styles.reorderableNavItemDragging;
      } else if (offset.x !== 0 || offset.y !== 0) {
        shiftClass = styles.reorderableNavItemShifted;
      }
    }
    return shiftClass;
  };

  const offsets = getOffsets();

  return (
    <>
      {list.map((item, index) => (
        <Nav.Item key={item.id}>
          <div
            draggable={canReorder}
            onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
              if (canReorder) {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", item.id);
                activeDragIndex.current = index;
                targetIndex.current = index;
                const layout = captureLayout(e.currentTarget);
                setDragImageFromTab(e);
                setDrag({ index, layout });
              }
            }}
            onDragEnd={commitDrag}
            className={classMerge([
              styles.reorderableNavItem,
              drag === null ? styles.reorderableNavItemStatic : "",
              canReorder ? styles.reorderableNavItemDraggable : "",
              getShiftClass(index, offsets[index]),
            ])}
            style={
              {
                "--mos-reorderable-dx": `${offsets[index].x}px`,
                "--mos-reorderable-dy": `${offsets[index].y}px`,
              } as React.CSSProperties
            }
            data-reorderable-index={index}
          >
            <Nav.Link
              eventKey={getEventKey?.(item, index) ?? `${index}`}
              disabled={disabled}
            >
              {item.name}
            </Nav.Link>
          </div>
        </Nav.Item>
      ))}
    </>
  );
};

ReorderableNavList.displayName = "ReorderableNavList";
